import { signal } from "@preact/signals";
import { menu, draft, menuLoading, screen, tableView, venueSwitchBlock } from "../state/store.js";
import { currentUser } from "../state/auth.js";
import { VenueSwitchBlock } from "./VenueSwitchBlock.js";
import { EnrollView } from "./EnrollView.js";
import { enrollToken } from "../state/enroll.js";
import { JoinView } from "./JoinView.js";
import { manualJoin } from "../state/join.js";
import { deviceUnassigned } from "../state/assignment.js";
import { getDeviceToken } from "../state/config.js";
import { StatusBar } from "./StatusBar.js";
import { kitchenOnline, deviceActivation, localOnline } from "../state/sync.js";
import { TablePad } from "./TablePad.js";

/** Klartext-Hinweis, wenn das Gerät im Cloud-Betrieb (noch) nicht senden darf. */
const ACTIVATION_MSG: Record<string, string> = {
  "not-registered": "Gerät nicht registriert — bitte den Einrichtungs-QR scannen.",
  "not-assigned": "Gerät keiner Veranstaltung zugewiesen — bitte den QR der Veranstaltung erneut scannen.",
  pending: "Gerät wartet auf Freischaltung — der Betreiber muss es im Admin aktivieren. Bestellungen können noch nicht gesendet werden.",
  blocked: "Dieses Gerät ist gesperrt — es kann keine Bestellungen senden.",
};
import { OrderingView } from "./OrderingView.js";
import { PaymentView } from "./PaymentView.js";
import { MyBons } from "./MyBons.js";
import { Login } from "./Login.js";
import { SettingsModal } from "./SettingsModal.js";
import { AdminView } from "./admin/AdminView.js";

const showSettings = signal(false);

export function App() {
  // Registrierungs-Modus (Betreiber-QR „Handy hinzufügen"): vor allem anderen.
  if (enrollToken) return <EnrollView />;
  // Noch kein Geräte-Token → Code-Screen (verbindet das Gerät mit Event/Betrieb).
  if (!getDeviceToken()) return <JoinView />;
  // „Gerät neu einrichten" aus dem Login (Event wechseln).
  if (manualJoin.value) return <JoinView manual />;
  // Venue-Wechsel mit offenen, nicht gesendeten Bons → erst sichern (vor allem anderen).
  if (venueSwitchBlock.value) return <VenueSwitchBlock />;
  // Keiner Veranstaltung zugewiesen → sauberer Login (kein Personal/Karte vom letzten Event).
  if (deviceUnassigned.value) return <Login />;
  // Anmeldung ist Pflicht.
  if (!currentUser.value) return <Login />;

  // In-App-Verwaltung (nur Personal-Admins) — eigener Vollbild-Screen mit eigener Kopfzeile.
  if (screen.value === "admin" && currentUser.value.role === "admin") {
    return (
      <div class="app">
        <AdminView />
      </div>
    );
  }

  return (
    <div class="app">
      <StatusBar onSettings={() => (showSettings.value = true)} />
      {(deviceActivation.value === "blocked" || !localOnline.value) && ACTIVATION_MSG[deviceActivation.value] && (
        <div class="kitchen-warning" role="alert">
          ⚠️ {ACTIVATION_MSG[deviceActivation.value]}
        </div>
      )}
      {/* Im WLAN kann ein nur „verfuegbares" Gerät lokal senden — kein Alarm, aber ein Hinweis,
          damit der Betreiber es aktiviert, bevor es auf Mobilfunk wechselt (und dort 401 bekommt). */}
      {localOnline.value && (deviceActivation.value === "pending" || deviceActivation.value === "not-assigned") && (
        <div class="kitchen-info" role="status">
          ℹ️ Gerät ist im WLAN einsatzbereit, für Mobilfunk aber noch nicht freigeschaltet — der
          Betreiber sollte es im Admin aktivieren.
        </div>
      )}
      {kitchenOnline.value === false && (
        <div class="kitchen-warning" role="alert">
          ⚠️ Küche offline — Bons werden gerade <b>nicht gedruckt</b>. Bestellungen werden
          gespeichert und nachgeholt, sobald der Server wieder verbunden ist.
        </div>
      )}
      <main class="main">
        {screen.value === "bons" ? (
          <MyBons />
        ) : !menu.value && menuLoading.value ? (
          <div class="empty">Lade Speisekarte…</div>
        ) : !menu.value ? (
          <div class="empty">
            Keine Speisekarte gefunden.
            <br />
            Prüfe Server-URL und Venue in den Einstellungen ⚙︎.
          </div>
        ) : draft.value && tableView.value === "pay" ? (
          <PaymentView />
        ) : draft.value && tableView.value === "order" ? (
          <OrderingView />
        ) : (
          <TablePad />
        )}
      </main>
      {showSettings.value && <SettingsModal onClose={() => (showSettings.value = false)} />}
    </div>
  );
}
